import React, { useEffect, useState } from "react";
import { faded_red, color_text_light, color_shades_darkest, color_variants_decline_light_1, color_colors_decline, color_colors_ocean, color_variants_shadow_20 } from "../../constants/colors";
import styled from "@emotion/styled";
import Button from "../general/Button";
import { css } from "@emotion/css";
import ModalHeader from "../general/ModalHeader";
import FooterBar from "../structure/FooterBar";
import CustomScrollbars from "../structure/CustomScrollbars";
import useWindowSize from "../../hooks/UseWindowSize";
import TemplateFormFieldValueModel from "../../models/templating/api/TemplateFormFieldValueModel";
import ConditionalTooltipWrapper from "../general/ConditionalTooltipWrapper";
import SmallModalLayout from "../structure/SmallModalLayout";

const alertIcon = require('../../content/images/alert-circle.svg');
const modifiedIcon = require('../../content/images//yellow-update.svg');
const addedIcon = require('../../content/images/green-check-circle-2.svg');
const removedIcon = require('../../content/images/red-no-circle.svg');
const schemaUpdateIcon = require('../../content/images/assistant-blue.svg');

export interface ChangedFieldModel {
    fieldName: string;
    oldValue: string;
    newValue: string;
    state: "Added" | "Modified" | "Removed";
}

export interface TemplateSaveChangesModalProps {
    showSaveChangesModal: boolean;
    closeModal: () => void;
    changedFields?: ChangedFieldModel[];
    schemaChanges?: Array<ChangedFieldModel & { isBlocker?: boolean }>;
    submitChanges: () => void;
    isLoading: boolean;
    hasErrors?: boolean;
    isFinishAction?: boolean;
    hideExplanation?: boolean;
}

const TemplateSaveChangesModal: React.FC<TemplateSaveChangesModalProps> = (props) => {
    const windowSize = useWindowSize();

    const [changedFields, setChangedFields] = useState<ChangedFieldModel[]>([]);

    useEffect(() => {
        if (!props.isLoading && props.showSaveChangesModal) {
            setChangedFields(props.changedFields || []);
        }
    }, [props.changedFields, props.showSaveChangesModal, props.isLoading]);

    const saveChangesAndClose = () => {
        props.submitChanges();
        props.closeModal();
    }

    const renderFieldChange = (change: ChangedFieldModel, index: number) => {
        return (
            <ChangedItemWrapper key={"change-" + index}>
                <div className={change.state === "Removed" ? removedItem : addedOrModifiedItem}>
                    {change.state}
                </div>
                <ChangedFieldText>
                    <IconAndFieldNameWrapper>
                        <Icon src={change.state === "Modified" ? modifiedIcon : change.state === "Added" ? addedIcon : removedIcon} />
                        <FieldName>{change.fieldName}</FieldName>
                    </IconAndFieldNameWrapper>
                    {change.state === "Modified" && (
                        <ValueChange>
                            "{change.oldValue}" → "{change.newValue}"
                        </ValueChange>
                    )}
                    {change.state === "Added" && change.newValue && (
                        <ValueChange>
                            Value: "{change.newValue}"
                        </ValueChange>
                    )}
                    {change.state === "Removed" && change.oldValue && (
                        <ValueChange>
                            Was: "{change.oldValue}"
                        </ValueChange>
                    )}
                </ChangedFieldText>
            </ChangedItemWrapper>
        );
    };

    const renderButtonText = () => {
        if (changedFields?.length > 0) {
            return "Save"
        } else {
            return "Sync";
        }
    }

    const renderSchemaChange = (change: ChangedFieldModel & { isBlocker?: boolean }, index: number) => {
        return (
            <ChangedItemWrapper key={"schema-change-" + index}>
                <div className={schemaChangeStateLabel}>
                    {change.state}
                </div>
                <ChangedFieldText>
                    <IconAndFieldNameWrapper>
                        <Icon src={change.isBlocker ? alertIcon : schemaUpdateIcon} />
                        <FieldName>{change.fieldName}</FieldName>
                    </IconAndFieldNameWrapper>
                    {change.isBlocker && (
                        <ValueChange>Required — needs a value before syncing</ValueChange>
                    )}
                    {!change.isBlocker && change.state === "Added" && change.newValue && (
                        <ValueChange>New default: "{change.newValue}"</ValueChange>
                    )}
                    {!change.isBlocker && change.state === "Modified" && (
                        <ValueChange>Default changed: "{change.oldValue}" → "{change.newValue}"</ValueChange>
                    )}
                    {change.state === "Removed" && change.oldValue && (
                        <ValueChange>Was: "{change.oldValue}"</ValueChange>
                    )}
                </ChangedFieldText>
            </ChangedItemWrapper>
        );
    };

    const renderExplanationText = () => {
        if (props.isFinishAction && changedFields?.length > 0) {
            return (
                <ExplanationText>
                    This will apply all updates to your live assistant. Once published, your assistant will begin using the new configuration.
                </ExplanationText>
            );
        } else if (changedFields?.length > 0) {
            return (
                <ExplanationText>
                    This will save your progress in the form, but it won't update your assistant yet. To apply these changes to your live assistant, go to the Review section and click Finish.
                </ExplanationText>
            );
        } else {
            return (
                <ExplanationText>
                    It looks like your form doesn't have any unsaved updates.<br /><br />Sync to update your assistant with the latest Voicify enhancements.
                </ExplanationText>
            );
        }
    };

    let tooltip = "";
    if (!props.isLoading) {
        if (props.hasErrors) {
            tooltip = "You have errors in your form";
        } else if (props.schemaChanges?.some(c => c.isBlocker)) {
            tooltip = "New required fields need values before syncing";
        } else if (changedFields?.length === 0) {
            tooltip = "You don't have any changes to save";
        }
    }

    const hasBlockingSchemaChanges = props.schemaChanges?.some(c => c.isBlocker) ?? false;
    const schemaChangesPresent = (props.schemaChanges?.length ?? 0) > 0;
    const changesPresent = changedFields?.length > 0;

    return (
        <SmallModalLayout className={saveChangesModal} isVisible={props.showSaveChangesModal}>
            <ModalHeader
                title={props.hideExplanation || changesPresent ? "Save Changes" : "Sync Assistant"}
                onClose={() => props.closeModal()}
            />
            {props.hasErrors && !props.isLoading && (
                <ErrorStyle>
                    <AlertIcon src={alertIcon} />
                    You have errors in your template form
                </ErrorStyle>
            )}
            <SaveChangesWrapper>
                {changesPresent && <Title>Save All Changes?</Title>}
                {!props.hideExplanation && renderExplanationText()}
                {changesPresent && <ChangedText>Here's what's changed:</ChangedText>}
                <ChangeSummary>
                    <CustomScrollbars
                        autoHide
                        autoHeight
                        autoHeightMin={(windowSize.windowSize.innerHeight * .6) - 190}
                        autoHeightMax={(windowSize.windowSize.innerHeight * .6) - 190}
                    >
                        <div>
                            {changedFields?.map((change, index) => renderFieldChange(change, index))}
                            {schemaChangesPresent && (
                                <SchemaUpdatesSection>
                                    <SchemaUpdatesTitle hasBlocker={hasBlockingSchemaChanges}>
                                        {hasBlockingSchemaChanges
                                            ? "Module Updates — Action Required"
                                            : "Module Updates Available"}
                                    </SchemaUpdatesTitle>
                                    <SchemaUpdatesDescription>
                                        {hasBlockingSchemaChanges
                                            ? "New required fields have been added to this module. Please go back and fill them in before syncing."
                                            : "The source module has been updated. New defaults will be applied automatically when you sync."}
                                    </SchemaUpdatesDescription>
                                    {props.schemaChanges?.map((change, index) => renderSchemaChange(change, index))}
                                </SchemaUpdatesSection>
                            )}
                        </div>
                    </CustomScrollbars>
                </ChangeSummary>
            </SaveChangesWrapper>
            <FooterBar>
                <ConditionalTooltipWrapper
                    place="top"
                    showTooltip={!!tooltip}
                    text={tooltip}
                >
                    <SaveButtonWrapper>
                        <Button
                            type="button"
                            themes={["primary"]}
                            text={renderButtonText()}
                            loading={props.isLoading}
                            disabled={props.isLoading || props.hasErrors || hasBlockingSchemaChanges || (props.hideExplanation && !changesPresent)}
                            onClick={() => saveChangesAndClose()}
                        />
                    </SaveButtonWrapper>
                </ConditionalTooltipWrapper>
                <Button
                    type="button"
                    themes={["secondary"]}
                    text="Cancel"
                    disabled={props.isLoading}
                    onClick={() => props.closeModal()}
                />
            </FooterBar>
        </SmallModalLayout>
    );
};

export default TemplateSaveChangesModal;

const IconAndFieldNameWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const FieldName = styled.div``;

const ChangeSummary = styled.div`
    padding: 16px 0;
`;

const Title = styled.div`
    margin-bottom: 16px;
`;

const SaveChangesWrapper = styled.div`
    padding: 32px;
`;

const ChangedFieldText = styled.div`
    font-style: italic;
    display: flex;
    flex-direction: column;
`;

const ValueChange = styled.div`
    font-size: 12px;
    color: ${color_text_light};
    margin-top: 4px;
    margin-left: 24px;
`;

const SaveButtonWrapper = styled.div`
    margin-right: auto;
`;

const saveChangesModal = css`
    height: 80%;
    width: 30%;
    min-width: 450px;
`;

const ExplanationText = styled.p`
    font-size: 14px;
    margin-bottom: 16px;
`;

const ChangedText = styled.p`
    margin-top: 32px;
    margin-bottom: 0;
    font-weight: bold;
`;

const ChangedItemWrapper = styled.div`
    padding-top: 16px;
    display: flex;
    align-items: flex-start;
    &:first-of-type {
        padding-top: 0;
    }
`;

const addedOrModifiedItem = css`
    color: ${color_text_light};
    font-style: italic;
    min-width: 80px;
`;

const removedItem = css`
    color: ${faded_red};
    font-style: italic;
    min-width: 80px;
`;

const schemaChangeStateLabel = css`
    color: ${color_colors_ocean};
    font-style: italic;
    min-width: 80px;
`;

const Icon = styled.img`
    padding: 0 8px;
    width: 32px;
`;

const ErrorStyle = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-family: Muli, "sans-serif";
    font-weight: normal;
    color: ${color_shades_darkest};
    background-color: ${color_variants_decline_light_1};
    border: 1px solid ${color_colors_decline};
    border-radius: 8px;
    flex-grow: 1;
    padding: 8px 16px;
    margin: 8px 0;
    font-size: 14px;
    margin: 16px 32px 0px 32px;
`;

const AlertIcon = styled.img`
    margin-right: 8px;
`;

const SchemaUpdatesSection = styled.div`
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid ${color_variants_shadow_20};
`;

const SchemaUpdatesTitle = styled.p<{ hasBlocker?: boolean }>`
    font-weight: bold;
    margin-bottom: 4px;
    color: ${props => props.hasBlocker ? color_colors_decline : color_colors_ocean};
`;

const SchemaUpdatesDescription = styled.p`
    font-size: 13px;
    color: ${color_text_light};
    margin-bottom: 12px;
`;
